import React, { useState } from 'react';
import { X, Loader2, Upload, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobApplicationModal = ({ isOpen, onClose, job }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    cover_letter: '',
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.pdf', '.doc', '.docx'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        setError('Please upload a PDF or Word document');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setResume(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const submitData = new FormData();
      submitData.append('full_name', formData.full_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('linkedin_url', formData.linkedin_url || '');
      submitData.append('cover_letter', formData.cover_letter || '');
      submitData.append('job_id', job.id);
      submitData.append('job_title', job.title);
      submitData.append('company', job.company);
      
      if (resume) {
        submitData.append('resume', resume);
      }

      const response = await fetch(`${API_URL}/api/applications`, {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit application');
      }

      setIsSuccess(true);
      
      // Reset form after 3 seconds and close
      setTimeout(() => {
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          linkedin_url: '',
          cover_letter: '',
        });
        setResume(null);
        setIsSuccess(false);
        onClose();
      }, 3000);

    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0A1F44] text-white p-6 rounded-t-2xl">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            data-testid="close-application-modal"
          >
            <X className="h-6 w-6" />
          </button>
          <h2 className="text-2xl font-bold pr-8">Apply for Position</h2>
          <p className="text-gray-300 mt-1">{job?.title}</p>
          <p className="text-[#D4AF37] text-sm mt-1">{job?.company}</p>
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-[#0A1F44] mb-2">Application Submitted!</h3>
            <p className="text-gray-600">
              Thank you for your interest in this position. Our team will review your application and contact you soon.
            </p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Name */}
            <div>
              <Label htmlFor="full_name" className="text-[#0A1F44] font-medium">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                required
                value={formData.full_name}
                onChange={handleChange}
                placeholder="John Doe"
                className="mt-1"
                data-testid="application-name-input"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-[#0A1F44] font-medium">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className="mt-1"
                data-testid="application-email-input"
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-[#0A1F44] font-medium">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 (555) 000-0000"
                className="mt-1"
                data-testid="application-phone-input"
              />
            </div>

            {/* LinkedIn */}
            <div>
              <Label htmlFor="linkedin_url" className="text-[#0A1F44] font-medium">
                LinkedIn Profile
              </Label>
              <Input
                id="linkedin_url"
                name="linkedin_url"
                type="url"
                value={formData.linkedin_url}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/yourprofile"
                className="mt-1"
                data-testid="application-linkedin-input"
              />
            </div>

            {/* Resume Upload */}
            <div>
              <Label htmlFor="resume" className="text-[#0A1F44] font-medium">
                Resume <span className="text-red-500">*</span>
              </Label>
              <div className="mt-1">
                <label 
                  htmlFor="resume" 
                  className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all"
                >
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">
                    {resume ? resume.name : 'Upload Resume (PDF or Word)'}
                  </span>
                </label>
                <input
                  id="resume"
                  name="resume"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  required
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="application-resume-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Max file size: 5MB</p>
            </div>

            {/* Cover Letter */}
            <div>
              <Label htmlFor="cover_letter" className="text-[#0A1F44] font-medium">
                Why are you interested in this role?
              </Label>
              <Textarea
                id="cover_letter"
                name="cover_letter"
                value={formData.cover_letter}
                onChange={handleChange}
                placeholder="Tell us a bit about yourself and why you're a great fit..."
                className="mt-1 min-h-[100px]"
                data-testid="application-cover-letter-input"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432] font-semibold py-6"
              data-testid="submit-application-btn"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Submitting...
                </span>
              ) : (
                'Submit Application'
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              By submitting, you agree to be contacted about this and future opportunities.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default JobApplicationModal;
